<template lang="pug">
  div.yz-phone-input-wrapper(:class="{ 'is-focused': isFocused }")
    v-row(no-gutters)
      v-col.shrink
        v-select.phoneLeft(:class="[sizeClass, { 'sync-focus': isFocused && !isSelectFocused }]" v-model="dialCode" hide-details :error="errorField" @focus="onSelectFocus" @blur="onSelectBlur" @keypress="searchCountry($event)" :items="countryList" item-value="iso" append-icon="$yzi-chevron-down" outlined :disabled="disabled" :background-color="backgroundColor")
          template(v-slot:selection="{ item, index }")
            div.d-flex.align-center
              div.mr-1(style="height: 18px;")
                img(:src="require(`@/assets/images/flags/${item.iso}.png`)" width="30px" height="18px")
              div +{{ item.dialCode }}
          template(v-slot:item="{ item, on, attrs }")
            v-row(no-gutters v-on="on" align="center")
              v-col.shrink.pr-1
                div(style="height: 18px;")
                  img(:src="require(`@/assets/images/flags/${item.iso}.png`)" width="30px" height="18px")
              v-col.pl-1
                div(style="line-height: 20px;") {{ item.name }}
                  strong.ml-2 +{{ item.dialCode }}
      v-col
        v-text-field.phoneRight.round-lg.yz-regular-body-sm(:class="[sizeClass, { 'sync-focus': isFocused && !isTextFocused }]" ref="dial" v-model="dialNumber" :placeholder="placeholder || $t('phone_number')" hide-details :rules="computedRules" :error="errorField" @keypress="isNumber($event)" @keyup="checkError" type="tel" @focus="onTextFocus" @blur="onTextBlur" outlined :disabled="disabled" :background-color="backgroundColor")
    v-progress-linear(v-if="loading" height="2" rounded color="primary" indeterminate)
    .yz-regular-body-xs.ml-3.error--text.mt-2.mb-3(v-if="computedErrorMessage") {{ computedErrorMessage }}
    div.mt-3(v-else)
</template>

<script>
import {DateTime} from 'luxon'
import {countries} from '@/assets/phoneCodeCountries.js'

export default {
  name: 'YzPhoneInput',
  props: {
    value: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    backgroundColor: {
      type: String,
      default: 'white'
    },
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['lg', 'md', 'sm'].includes(value)
    },
    requiredErrorMessage: {
      type: String,
      default: 'This field is required'
    }
  },
  data: () => ({
    errorField: null,
    dialCode: 'my',
    dialNumber: '',
    searchString: '',
    phoneAlertMessage: '',
    filteredCountry: [],
    isFocused: false,
    isSelectFocused: false,
    isTextFocused: false,
    hasBeenValidated: false,
    internalErrorMessages: [],
    bgColor: 'basic200'
  }),
  computed: {
    countryList() {
      if (this.filteredCountry.length > 0) {
        return this.filteredCountry
      }
      return countries
    },
    phoneNumber() {
      let dialcode = 6
      const filteredCountry = this.countryList.find(f => f.iso === this.dialCode)
      if (filteredCountry) {
        dialcode = filteredCountry.dialCode
      }
      if (this.dialNumber.startsWith(dialcode)) {
        return this.dialNumber
      }
      return dialcode + this.dialNumber
    },
    sizeClass() {
      return `yz-phone-input--${this.size}`
    },
    // Define validation rules as computed properties to access this.requiredErrorMessage
    validationRules() {
      return {
        phone: [
          v => !!v || this.requiredErrorMessage,
          v => /^(01)[0-46-9]-*[0-9]{7,8}$/.test(v) || /^(0)[3-9]-*[0-9]{7,8}$/.test(v) || 'Phone number must be valid'
        ],
        phone_inter: [v => !!v || this.requiredErrorMessage],
        phone_optional: [
          v => !v || /^(01)[0-46-9]-*[0-9]{7,8}$/.test(v) || /^(0)[3-9]-*[0-9]{7,8}$/.test(v) || 'Phone number must be valid'
        ],
        phone_inter_optional: [
          v => !v || v.length > 0 // For international numbers, just check if not empty when provided
        ]
      }
    },
    computedRules() {
      // If required, use the standard rules
      if (this.required) {
        return this.dialCode === 'my' ? this.validationRules.phone : this.validationRules.phone_inter
      }
      // If not required but has input, use optional validation rules
      if (this.dialNumber) {
        return this.dialCode === 'my' ? this.validationRules.phone_optional : this.validationRules.phone_inter_optional
      }
      // If not required and no input, no rules
      return []
    },
    computedErrorMessage() {
      if (this.phoneAlertMessage) {
        return this.phoneAlertMessage;
      }

      return ''
    },
    hasError() {
      return !!this.computedErrorMessage;
    }
  },
  watch: {
    dialNumber() {
      this.$nextTick(() => {
        this.validate(true);
      });
    },
    dialCode() {
      this.$nextTick(() => {
        this.validate(true);
      })
    },
    phoneNumber: {
      handler(val) {
        this.$emit('input', val)
        this.$emit('change')
      }
    }
  },
  mounted() {
    // Register with parent v-form if it exists
    this.form = this.getForm();
    if (this.form && this.form.register) {
      this.form.register(this);
    }

    this.$nextTick(() => {
      if (this.value) {
        const num = this.value
        for (let i = 4; i > 0; i--) {
          const country = this.countryList.filter(f => f.dialCode === num.substr(0, i) && f.priority === 0)
          if (country.length > 0) {
            this.dialCode = country[0].iso
            this.dialNumber = num.substr(i)
            break
          }
        }
      }
      if (!this.dialNumber) {
        const zoneId = DateTime.local().toFormat('z')
        const country = this.$options.filters.getCountryCodeFromZoneId(zoneId)
        const phoneCountry = this.countryList.filter(f => f.isoUpper === country)
        if (phoneCountry.length > 0) {
          this.dialCode = phoneCountry[0].iso
        }
      }
    })
  },
  beforeDestroy() {
    // Unregister from parent v-form
    if (this.form && this.form.unregister) {
      this.form.unregister(this);
    }
  },
  methods: {
    isNumber(evt) {
      evt = (evt) || window.event
      const charCode = (evt.which) ? evt.which : evt.keyCode
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault()
      } else {
        return true
      }
    },
    onSelectFocus() {
      this.isSelectFocused = true
      this.isFocused = true
      this.bgColor = 'white'
      this.$emit('focus', false)
    },
    onSelectBlur() {
      this.isSelectFocused = false
      this.isFocused = this.isTextFocused
      this.searchString = ''
      this.filteredCountry = []
      if (!this.isFocused) {
        this.bgColor = 'basic200'
      }
      this.$emit('blur', true)
    },
    onTextFocus() {
      this.isTextFocused = true
      this.isFocused = true
      this.bgColor = 'white'
      this.$emit('focus', false)
    },
    onTextBlur() {
      this.isTextFocused = false
      this.isFocused = this.isSelectFocused
      if (!this.isFocused) {
        this.bgColor = 'basic200'
      }

      // Trigger validation on blur for user feedback
      this.validate(true);
      this.checkError()
      this.$emit('blur', true)
    },
    searchCountry(evt) {
      if (this.searchString) {
        this.searchString += evt.key
        this.filteredCountry = countries.filter(f => f.name.toLowerCase().slice(0, this.searchString.length) === this.searchString)
        if (this.filteredCountry.length === 0) {
          this.searchString = ''
        }
      } else {
        this.searchString = evt.key
        this.filteredCountry = countries.filter(f => f.name.toLowerCase().slice(0, this.searchString.length) === this.searchString)
        if (this.filteredCountry.length === 0) {
          this.searchString = ''
        }
      }
    },
    // Main validation method that v-form will call
    validate(force = false) {
      if (force) {
        this.hasBeenValidated = true;
      }

      // Run validation logic and populate phoneAlertMessage for immediate display
      let isValid = true;
      this.phoneAlertMessage = '';

      const rules = this.computedRules;
      for (const rule of rules) {
        const result = rule(this.dialNumber);
        if (result !== true) {
          this.phoneAlertMessage = result;
          isValid = false;
          break;
        }
      }

      // Set error field state for both v-select and v-text-field
      this.errorField = !isValid;

      // Also let v-text-field handle validation for its internal state
      if (this.$refs.dial) {
        this.$refs.dial.validate();
      }

      return isValid;
    },
    checkError() {
      this.errorField = !this.$refs.dial.valid
    },
    // Just clear validation errors without resetting value
    resetValidation() {
      this.hasBeenValidated = false;
      this.internalErrorMessages = [];
      this.phoneAlertMessage = '';
      this.errorField = null;
      if (this.$refs.dial) {
        this.$refs.dial.resetValidation();
      }
    },
    // Helper method to find parent v-form
    getForm() {
      let parent = this.$parent;

      while (parent) {
        // Check if it's a v-form by name or by checking for form methods
        if (parent.$options.name === 'v-form' || (parent.register && parent.unregister && parent.validate)) {
          return parent;
        }

        // Also check if parent has a form ref (for custom form wrappers)
        if (parent.$refs && parent.$refs.form &&
            parent.$refs.form.register && parent.$refs.form.unregister) {
          return parent.$refs.form;
        }

        parent = parent.$parent;
      }

      return null;
    }
  }
}
</script>

<style lang="scss" scoped>
.yz-phone-input-wrapper ::v-deep {
  .error--text {
    fieldset {
      border-color: var(--yz-danger) !important;
    }
  }

  // Error state for v-select
  .phoneLeft.error--text fieldset,
  .phoneLeft[error="true"] fieldset {
    border-color: var(--yz-danger) !important;
  }

  // Error state for v-text-field
  .phoneRight.error--text fieldset {
    border-color: var(--yz-danger) !important;
  }

  // Left side (v-select) styling
  .phoneLeft {
    // Match border color with text field (only when not in error or focused state)
    &.v-input--outlined:not(.v-input--is-focused):not(.error--text):not([error="true"]):not(.sync-focus) fieldset {
      border-color: var(--yz-neutral-400) !important;
    }

    fieldset {
      border-radius: 10px 0 0 10px !important;
      border-right: 0 !important;
    }

    .v-input__slot {
      padding-right: 0px !important;
    }

    // Focus state for phoneLeft (only when not in error state)
    &.v-input--is-focused:not(.error--text):not([error="true"]) fieldset {
      border-color: var(--yz-neutral-700) !important;
    }

    // Synchronized focus state (when text field is focused)
    &.sync-focus:not(.error--text):not([error="true"]) fieldset {
      border-color: var(--yz-neutral-700) !important;
    }

    // Size-specific heights for v-select to match yz-text-field
    &.yz-phone-input--lg {
      .v-input__slot {
        min-height: 36px !important;
        height: 36px !important;
      }

      .v-select__selection {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }

      .v-input__append-inner {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        height: 36px !important;
        display: flex !important;
        align-items: center !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
    }

    &.yz-phone-input--md {
      .v-input__slot {
        min-height: 32px !important;
        height: 32px !important;
      }

      .v-select__selection {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }

      .v-input__append-inner {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        height: 32px !important;
        display: flex !important;
        align-items: center !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
    }

    &.yz-phone-input--sm {
      .v-input__slot {
        min-height: 28px !important;
        height: 28px !important;
      }

      .v-select__selection {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        font-size: 0.875rem;
      }

      .v-input__append-inner {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        height: 28px !important;
        display: flex !important;
        align-items: center !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
    }

    // Ensure icon container doesn't add extra padding
    .v-input__append-inner {
      .v-input__icon {
        height: 100% !important;
        display: flex !important;
        align-items: center !important;
        margin: 0 !important;
        padding: 0 !important;
      }
    }
  }

  // Right side (v-text-field) styling - Now using v-text-field directly
  .phoneRight {
    // Default border styling (only when not in error or focused state)
    &.v-text-field--outlined:not(.v-input--is-focused):not(.error--text):not(.sync-focus) fieldset {
      border-radius: 0 10px 10px 0 !important;
      border-left: 0 !important;
      border-color: var(--yz-neutral-400) !important;
    }

    // Error state border
    &.v-text-field--outlined.error--text fieldset {
      border-radius: 0 10px 10px 0 !important;
      border-left: 0 !important;
      border-color: var(--yz-danger) !important;
    }

    // Focus state for phoneRight (only when not in error state)
    &.v-input--is-focused:not(.error--text) fieldset {
      border-radius: 0 10px 10px 0 !important;
      border-left: 0 !important;
      border-color: var(--yz-neutral-700) !important;
    }

    // Synchronized focus state (when select is focused)
    &.sync-focus:not(.error--text) fieldset {
      border-radius: 0 10px 10px 0 !important;
      border-left: 0 !important;
      border-color: var(--yz-neutral-700) !important;
    }

    // Focus state when in error
    &.v-input--is-focused.error--text fieldset {
      border-radius: 0 10px 10px 0 !important;
      border-left: 0 !important;
      border-color: var(--yz-danger) !important;
    }

    // Size-specific styles matching yz-text-field
    &.yz-phone-input--lg {
      .v-input__slot {
        min-height: 36px !important;
        height: 36px !important;
      }

      input {
        height: 36px !important;
      }
    }

    &.yz-phone-input--md {
      .v-input__slot {
        min-height: 32px !important;
        height: 32px !important;
      }

      input {
        height: 32px !important;
      }
    }

    &.yz-phone-input--sm {
      .v-input__slot {
        min-height: 28px !important;
        height: 28px !important;
      }

      input {
        height: 28px !important;
      }
    }
  }

  // Icon styling for all sizes
  .v-icon__component {
    height: 18px !important;
    width: 18px !important;
  }

  .v-input__icon {
    .v-icon {
      height: 18px !important;
      width: 18px !important;
      font-size: 18px !important;
    }
  }

  // Ensure both inputs align properly
  .v-input--outlined {
    .v-input__slot {
      align-items: center;
    }
  }
}</style>